import { v4 as uuidv4 } from 'uuid';
import http, { endpoints } from '../http';
import { mapUploadPartUrlFromDto, planUploadParts, sha256Base64, UPLOAD_HASH_ALGORITHM, UPLOAD_PART_SIZE_BYTES } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
const bareClient = http.createClient('');
/** The signed PUT is an absolute S3 URL — the signature covers these two headers, so both are required. */
const S3_PART_HEADERS = (contentHash) => ({
    'Content-Type': 'application/octet-stream',
    'x-amz-checksum-sha256': contentHash,
});
/**
 * Efkt's replacement for `datahub/s3`. Same job — get a browser-side blob into object storage and hand
 * back a URL — but a five-step saga (initiate -> part URLs -> PUT parts -> progress -> complete ->
 * poll status) instead of datahub's single presigned PUT, and chunked, so a large file goes up in
 * 16 MB parts that can be re-sent individually.
 *
 * **Two things about this domain are different from the rest of the package, and both matter.**
 *
 * First, the saga itself is *real* and was read off the backend, not guessed: the routes, bodies and
 * response shapes in `dtos.ts` are transcribed from `Efkt/Services/Assets/MultipartUpload/`, and the
 * one working client (`Efkt/Tools/Workbench/Workers/Sales/ManageSalesOrderWorker.cs`) supplied the two
 * details the route signatures do not — `correlationId` equals `fileUploadId`, and the *caller* mints
 * both, because initiate does not echo the id back.
 *
 * Second — **EFKT-BE-GAP** — it is not reachable from a browser yet.
 * `Efkt/Applications/Gateway/Frontend/Endpoints/**` exposes no `/api/assets/uploads/*` route at all;
 * the only upload routes on the fe-gateway are the job-bound photographer ones
 * (`/api/photographer/jobs/{jobId}/uploads/...`), which the webshop must not call — that `jobId` is
 * authorization and backend routing for the photographer app, not part of an upload. BE's own words:
 * "the job-free version already exists, but it isn't callable from the browser yet (no fe-gateway
 * route)". Wiring it up is a gateway passthrough, not a contract change, so this client is written
 * against the final shape and starts working the day the route lands.
 *
 * The second **EFKT-BE-GAP** is `createAsset` — see its own note.
 *
 * **Both gaps fail loudly, on purpose.** An Efkt-owned record uploads through here and nowhere else:
 * there is no fallback to `datahub/s3`, because an asset written to datahub storage for an Efkt record
 * belongs to neither backend cleanly. `uploadFile` fails on its first request (`createAsset`), before
 * any byte moves and before any saga is started, so a gap shows up as one 404 and no orphaned state.
 */
export default class AssetsApi {
    /**
     * EFKT-BE-GAP — guessed path. **This is the blocking gap, not a cosmetic one.**
     *
     * `POST /api/assets/uploads` 404s with "Asset not found" unless the asset row already exists, and
     * nothing creates one over HTTP: the only producer is the `CreateAssetConsumer` bus consumer, which
     * the workbench reaches by calling `ICreateAsset` in-process. The BE team named this explicitly —
     * "nothing creates the asset row over HTTP, so it's not a URL swap, we owe you a surface first".
     *
     * `POST /api/assets` is the shape that surface would most plausibly take, so the rest of the flow
     * below is written as if it exists. Until it does, `uploadFile` fails here, on its first request,
     * which is the right place for it to fail: no partial upload, no orphaned saga.
     *
     * Everything downstream of this one call is real and read off the backend, so when BE ships an
     * asset-creation route the change is this method's path and body — the saga below is untouched.
     */
    static async createAsset(type = 'Image') {
        const response = await efktClient.post('/api/assets', { type });
        return response.data.assetId;
    }
    /**
     * `POST /api/assets/uploads` — 202, body `{ assetId, fileName, expires }`. Returns the id the
     * caller minted, because the response does not carry it (see `IInitiateUploadRequestDto`).
     */
    static async initiateUpload(assetId, fileName, expiresInDays = 1) {
        const fileUploadId = uuidv4();
        const expires = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString();
        const body = {
            correlationId: fileUploadId,
            fileUploadId,
            assetId,
            fileName,
            public: true,
            expires,
        };
        await efktClient.post('/api/assets/uploads', body);
        return fileUploadId;
    }
    /**
     * `GET /api/assets/uploads/upload-urls/{fileUploadId}?part=1&part=2`.
     *
     * Part numbers are **repeated `part` params, not a comma-separated list**, which is why the query
     * is built by hand rather than handed to axios' default serializer (that one emits `part[]=1`).
     * Omitting `part` entirely faults as a 500 rather than a 400 server-side, so never call this with
     * an empty list.
     */
    static async getUploadPartUrls(fileUploadId, partNumbers) {
        const query = partNumbers.map(part => `part=${part}`).join('&');
        const response = await efktClient.get(`/api/assets/uploads/upload-urls/${encodeURIComponent(fileUploadId)}?${query}`);
        return response.data.map(mapUploadPartUrlFromDto);
    }
    /** Raw PUT of one part to its signed URL. Returns the ETag, which `submitProgress` has to report. */
    static async uploadPart(signedUrl, part, contentHash) {
        const { headers } = await bareClient.put(signedUrl, part, { headers: S3_PART_HEADERS(contentHash) });
        return headers.etag;
    }
    /**
     * `POST /api/assets/uploads/progress` — records one part so an interrupted upload can resume.
     *
     * Fire-and-forget on the backend: it answers 200 as soon as the event is published, before the saga
     * has consumed it, so a 200 means "accepted", not "persisted".
     */
    static async submitProgress(params) {
        const body = {
            ...params,
            correlationId: params.fileUploadId,
            hashAlgorithm: UPLOAD_HASH_ALGORITHM,
        };
        await efktClient.post('/api/assets/uploads/progress', body);
    }
    /** `POST /api/assets/uploads/complete` — 202. Assembly is asynchronous; `getFinishedUpload` waits for it. */
    static async completeUpload(fileUploadId, hash) {
        const body = {
            correlationId: fileUploadId,
            fileUploadId,
            hash,
            hashAlgorithm: UPLOAD_HASH_ALGORITHM,
        };
        await efktClient.post('/api/assets/uploads/complete', body);
    }
    /**
     * `GET /api/assets/uploads/{fileUploadId}/status` — the completion wait.
     *
     * Implemented backend-side as a request/response over the bus rather than a database read, so it
     * blocks until the saga answers and **times out rather than 404s** if the upload never completed.
     * That is why there is no polling loop here: one call is the wait.
     */
    static async getFinishedUpload(fileUploadId) {
        const response = await efktClient.get(`/api/assets/uploads/${encodeURIComponent(fileUploadId)}/status`);
        return response.data;
    }
    /**
     * The whole saga for one blob, which is what callers actually want.
     *
     * Parts are read, hashed and PUT **in order, one at a time** — mirroring the backend's own
     * `FilePartUploader` — so peak memory is one part, not the file. Progress is reported per part
     * after its PUT lands, so an interrupted upload resumes from the last reported part.
     */
    static async uploadFile({ file, fileName, onProgress }) {
        const assetId = await AssetsApi.createAsset('Image');
        const fileUploadId = await AssetsApi.initiateUpload(assetId, fileName);
        const partNumbers = planUploadParts(file.size);
        const partUrls = await AssetsApi.getUploadPartUrls(fileUploadId, partNumbers);
        const signedUrlByPart = new Map(partUrls.map(({ partNumber, signedUrl }) => [partNumber, signedUrl]));
        for (const partNumber of partNumbers) {
            const signedUrl = signedUrlByPart.get(partNumber);
            // The batch signing came back short of what was asked for — a broken contract, not a retry.
            if (!signedUrl)
                throw new Error(`Efkt signed no upload URL for part ${partNumber}`);
            const start = (partNumber - 1) * UPLOAD_PART_SIZE_BYTES;
            const part = file.slice(start, start + UPLOAD_PART_SIZE_BYTES);
            const bytes = await part.arrayBuffer();
            const hash = await sha256Base64(bytes);
            const eTag = await AssetsApi.uploadPart(signedUrl, part, hash);
            await AssetsApi.submitProgress({
                fileUploadId,
                partNumber,
                eTag,
                sizeInBytes: bytes.byteLength,
                hash,
            });
            onProgress === null || onProgress === void 0 ? void 0 : onProgress((partNumber / partNumbers.length) * 100);
        }
        await AssetsApi.completeUpload(fileUploadId, await sha256Base64(await file.arrayBuffer()));
        const finished = await AssetsApi.getFinishedUpload(fileUploadId);
        return {
            assetId: finished.assetId,
            assetFileId: finished.assetFileId,
            url: signedUrlByPart.get(1).split('?')[0],
        };
    }
}
